'use client'

import { useRouter } from 'next/navigation'
import { useEffect, useRef, useState } from 'react'
import { TabContentProps } from '..'
import { Building2, Loader2, Trophy } from 'lucide-react'

import { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

import { getOrganizationsAction } from '@/app/actions/organization'
import { getWins } from '@/app/actions/win'
import { HorizontalScrollControls } from '@/components/HorizontalScrollControls'
import { OrgCard } from '@/components/OrgCard'
import RichText from '@/components/RichText'
import WinTile from '@/components/WinsComps/WinTile'
import { RenderLink } from '@/heros/HeroCenter'
import { Organization, Win } from '@/payload-types'
import { cn } from '@/utilities/ui'

type CollectionProps = {} & TabContentProps

const RenderCollection = ({
  loading,
  error,
  scrollContainerRef,
  collection,
  collectionType,
}: {
  loading: boolean
  error: string | null
  scrollContainerRef: React.RefObject<HTMLDivElement | null>
  collection: Organization[] | Win[]
  collectionType?: string | null
}) => {
  const router = useRouter()

  if (loading) {
    return (
      <p className="flex size-full items-center justify-center gap-2">
        <span className="font-body text-sm text-foreground">Loading...</span>
        <Loader2 className="size-10 animate-spin text-foreground" />
      </p>
    )
  }
  if (error) {
    return (
      <p className="flex size-full items-center justify-center">
        <span className="font-body text-sm text-red-500">{error}</span>
      </p>
    )
  }
  return (
    <div
      ref={scrollContainerRef}
      className={cn(
        'scrollbar-hidden -mr-[max(1rem,calc((100vw-var(--breakpoint-wrapper))/2+1rem))] flex snap-x snap-mandatory scroll-pl-0 flex-row gap-6 overflow-x-auto pr-8',
        '[&>article]:w-full [&>article]:max-w-full [&>article]:min-w-90 md:[&>article]:min-w-[394px]',
      )}
      data-lenis-prevent-horizontal
    >
      {collection.map((item) =>
        collectionType === 'organizations' ? (
          <OrgCard
            key={item.id}
            className="snap-start"
            doc={item as Organization}
            forceWidth={true}
            onClick={() => {
              const org = item as Organization
              if (org.slug) {
                router.push(`/other-great-organizations?org=${encodeURIComponent(org.slug)}`)
              }
            }}
          />
        ) : (
          <WinTile key={item.id} className="snap-start" win={item as Win} />
        ),
      )}
    </div>
  )
}

const Collection: React.FC<CollectionProps> = ({ title, content, collectionType, link }) => {
  const [collection, setCollection] = useState<Organization[] | Win[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      setError(null)

      try {
        const data =
          collectionType === 'organizations'
            ? ((await getOrganizationsAction({ limit: 12 }))?.data ?? [])
            : await getWins({ limit: 12 })

        if (data === null) {
          setError('Failed to fetch data')
          return
        }

        setCollection(data)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An unexpected error occurred')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [collectionType])

  return (
    <div className="w-full">
      <div className="flex flex-col gap-10 md:flex-row">
        <div className="w-full shrink-0 md:w-1/3">
          <div className="flex size-16 items-center justify-center rounded-xl bg-evergreen-50 text-accent">
            {collectionType === 'wins' ? (
              <Trophy className="size-8 text-evergreen-600" />
            ) : (
              <Building2 className="size-10 text-evergreen-600" />
            )}
          </div>
          <h2 className="py-3 text-2xl font-semibold">{title}</h2>
          <RichText data={content as unknown as DefaultTypedEditorState} />
          <RenderLink link={link} color="evergreen" className="mt-4 font-medium" />
        </div>
        <div className="w-full min-w-0 md:w-2/3">
          <RenderCollection
            loading={loading}
            error={error}
            scrollContainerRef={scrollContainerRef}
            collection={collection}
            collectionType={collectionType}
          />
        </div>
      </div>
      <HorizontalScrollControls
        className="my-6 justify-end"
        scrollRef={scrollContainerRef as React.RefObject<HTMLDivElement>}
      />
    </div>
  )
}

export default Collection
